import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  User,
  Building,
  Database,
  Globe,
  Share2,
  MoreVertical,
  TestTube,
  Pencil,
  Trash2,
  RefreshCw,
  Loader2,
  Check,
  AlertCircle,
} from 'lucide-react'
import type { DataSource, DataSourceType } from '@/lib/api/types'
import { cn } from '@/lib/utils'

interface DataSourceCardProps {
  dataSource: DataSource
  onToggle: (id: string, enabled: boolean) => Promise<void>
  onTest: (id: string) => Promise<void>
  onEdit: (dataSource: DataSource) => void
  onDelete: (dataSource: DataSource) => void
  onRefresh: (id: string) => Promise<void>
  testResult?: { success: boolean; message: string } | null
  isToggling?: boolean
  isTesting?: boolean
  isRefreshing?: boolean
}

const typeIcons: Record<DataSourceType, React.ElementType> = {
  UserProfile: User,
  TenantInfo: Building,
  CRM: Database,
  CustomApi: Globe,
  SharePoint: Share2,
  Database: Database,
  ExcelOnline: Database,
}

const typeLabels: Record<DataSourceType, string> = {
  UserProfile: 'User Profile',
  TenantInfo: 'Tenant Info',
  CRM: 'CRM',
  CustomApi: 'Custom API',
  SharePoint: 'SharePoint',
  Database: 'Database',
  ExcelOnline: 'Excel Online',
}

const typeColors: Record<DataSourceType, string> = {
  UserProfile: 'text-blue-600 dark:text-blue-400',
  TenantInfo: 'text-purple-600 dark:text-purple-400',
  CRM: 'text-green-600 dark:text-green-400',
  CustomApi: 'text-orange-600 dark:text-orange-400',
  SharePoint: 'text-teal-600 dark:text-teal-400',
  Database: 'text-cyan-600 dark:text-cyan-400',
  ExcelOnline: 'text-emerald-600 dark:text-emerald-400',
}

export function DataSourceCard({
  dataSource,
  onToggle,
  onTest,
  onEdit,
  onDelete,
  onRefresh,
  testResult,
  isToggling,
  isTesting,
  isRefreshing,
}: DataSourceCardProps) {
  const [showTestResult, setShowTestResult] = useState(false)
  const Icon = typeIcons[dataSource.type]

  const handleToggle = async (checked: boolean) => {
    await onToggle(dataSource.id, checked)
  }

  const handleTest = async () => {
    setShowTestResult(true)
    await onTest(dataSource.id)
  }

  const formatLastRefreshed = (dateString?: string) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }

  return (
    <Card className="relative">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg bg-neutral-100 dark:bg-neutral-800', typeColors[dataSource.type])}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">{dataSource.name}</CardTitle>
                {dataSource.isSystemSource && (
                  <Badge variant="secondary" className="text-xs">
                    System
                  </Badge>
                )}
              </div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {typeLabels[dataSource.type]}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={dataSource.isEnabled}
              onCheckedChange={handleToggle}
              disabled={isToggling}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleTest} disabled={isTesting}>
                  {isTesting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onRefresh(dataSource.id)} disabled={isRefreshing}>
                  {isRefreshing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh Data
                </DropdownMenuItem>
                {!dataSource.isSystemSource && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onEdit(dataSource)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600 dark:text-red-400"
                      onClick={() => onDelete(dataSource)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {dataSource.description && (
          <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-3">
            {dataSource.description}
          </p>
        )}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <span className="text-neutral-500 dark:text-neutral-400">
              <span className="font-medium text-neutral-900 dark:text-neutral-100">
                {dataSource.fieldCount}
              </span>{' '}
              fields
            </span>
            <span className="text-neutral-500 dark:text-neutral-400">
              Cache:{' '}
              <span className="font-medium text-neutral-900 dark:text-neutral-100">
                {dataSource.cacheDurationMinutes}m
              </span>
            </span>
          </div>
          <span className="text-neutral-500 dark:text-neutral-400">
            Last refreshed: {formatLastRefreshed(dataSource.lastRefreshedAt)}
          </span>
        </div>
        {showTestResult && testResult && (
          <div
            className={cn(
              'mt-3 p-2 rounded-md flex items-center gap-2 text-sm',
              testResult.success
                ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300'
                : 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'
            )}
          >
            {testResult.success ? (
              <Check className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            {testResult.message}
          </div>
        )}
        {dataSource.integrationName && (
          <div className="mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-700">
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              Connected via: <span className="font-medium">{dataSource.integrationName}</span>
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
